// The MAX edit controls — kg/lb input + unit toggle + (optionally) a primary
// Save button + the destructive Clear. Extracted from EditMaxSheet so the
// SAME body renders in two places without drifting:
//   narrow → inside EditMaxSheet's Modal (Save lives in the modal header, so
//            showSave is omitted here).
//   wide   → inline in the master-detail pane, where there is no modal header
//            → showSave renders the primary Save button between the input and
//            Clear.
// Presentational only: the owner (MaxesView via useMaxEditor) holds the state
// + the PUT/DELETE orchestration and passes handlers in.
import {
  ActivityIndicator,
  Pressable,
  Text,
  TextInput,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { makeStyles } from "./maxesStyles";
import type { WeightUnit } from "./useMaxEditor";

export type MaxEditFormProps = {
  theme: UiTheme;
  value: string;
  onChangeValue: (next: string) => void;
  unit: WeightUnit;
  onToggleUnit: () => void;
  busy: "save" | "clear" | null;
  error: string | null;
  canClear: boolean;
  onClear: () => void;
  /** Render the primary Save button in the body (wide pane); narrow leaves it
   *  in the modal header. */
  showSave?: boolean;
  onSave?: () => void;
  autoFocus?: boolean;
};

export function MaxEditForm({
  theme,
  value,
  onChangeValue,
  unit,
  onToggleUnit,
  busy,
  error,
  canClear,
  onClear,
  showSave = false,
  onSave,
  autoFocus,
}: MaxEditFormProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  return (
    <View style={styles.editBody}>
      <View style={styles.inputRow}>
        <TextInput
          style={styles.input}
          keyboardType="decimal-pad"
          inputMode="decimal"
          placeholder="—"
          placeholderTextColor={theme.palette.ink3}
          value={value}
          onChangeText={onChangeValue}
          autoFocus={autoFocus}
        />
        <Pressable
          accessibilityRole="button"
          hitSlop={8}
          // It announced as a bare "KG": a name that says what it reads, never
          // what it is or what tapping it does. The label spells the unit out
          // (an abbreviation is not a word a screen reader can be trusted to
          // say), the hint carries the part that matters — the number comes
          // with it.
          accessibilityLabel={t("mobile.maxes.unitChipLabel", {
            unit: t(`mobile.maxes.unit${unit === "kg" ? "Kg" : "Lb"}`),
          })}
          accessibilityHint={t("mobile.maxes.unitChipHint")}
          onPress={onToggleUnit}
          style={({ pressed }) => [
            styles.unitChip,
            pressed && styles.unitChipPressed,
          ]}
        >
          <Text style={styles.unitChipText}>{unit.toUpperCase()}</Text>
        </Pressable>
      </View>
      {error !== null ? <Text style={styles.errorText}>{error}</Text> : null}
      {showSave ? (
        <Pressable
          accessibilityRole="button"
          hitSlop={8}
          onPress={onSave}
          disabled={busy !== null}
          style={({ pressed }) => [
            styles.saveBtn,
            pressed && styles.saveBtnPressed,
            busy !== null && styles.saveBtnDisabled,
          ]}
        >
          {busy === "save" ? (
            <ActivityIndicator size="small" color={theme.palette.inkOnBrand} />
          ) : (
            <Text style={styles.saveBtnText}>{t("mobile.maxes.editSave")}</Text>
          )}
        </Pressable>
      ) : null}
      <Pressable
        accessibilityRole="button"
        hitSlop={8}
        onPress={onClear}
        disabled={busy !== null || !canClear}
        style={({ pressed }) => [
          styles.clearAction,
          pressed && styles.clearActionPressed,
          (busy !== null || !canClear) && styles.clearActionDisabled,
        ]}
      >
        {busy === "clear" ? (
          <ActivityIndicator size="small" color={theme.palette.danger} />
        ) : (
          <Text style={styles.clearActionText}>
            {t("mobile.maxes.clearAction")}
          </Text>
        )}
      </Pressable>
    </View>
  );
}
